import { getDisplayedName, getProductId, isProductSet } from 'features/product-info';
import React from 'react';
import { connect } from 'react-redux';
import type { Dispatch, Action } from 'redux';
import { RootReducer } from 'store/root-reducer';
import { LayoutControlledTypography } from 'common/components/fronton-decorators';
import { CopyIcon } from '@fronton/icons-react';
import { analyticsUtils } from 'utils';
import { selectLoyaltyRewardData } from 'features/product-loyalty-rewards/selectors/loyalty-rewards';
import { ILoyaltyReward } from 'features/product-loyalty-rewards/types/reducer';
import { BonusIcon } from 'common/icons/bonus-icon';
import { getDeviceType } from 'common/selectors/device-type';
import { ReturnValueDeviceType } from 'entries/server/utils';
import { MarkerLayout, articleContainer, displayedNameContainer, copyIconClass } from './styles';
import { showNotification } from '@navi-toolbox/ui-components';
import { NotificationTemplates } from 'common/components/notification-template-factory/types';
import { Marker } from 'fronton-react';

export const DATA_QA_COPY_BUTTON = 'copy-button';
export const DATA_QA_PRODUCT_NAME = 'product-name';
export const DATA_QA_LOYALTY_REWARDS_MARKER = 'loyalty-rewards-marker';

type ProductTitleMappedState = {
  productId: string;
  displayedName: string;
  loyaltyRewards: ILoyaltyReward | null;
  isSet: boolean;
  deviceType: ReturnValueDeviceType;
};

type ProductTitleProps = {
  Htag?: React.ComponentType<React.HTMLAttributes<HTMLElement>> | keyof JSX.IntrinsicElements;
} & ProductTitleMappedState &
  React.HTMLAttributes<HTMLElement>;

export const ProductTitle: React.FC<ProductTitleProps> = ({
  productId,
  displayedName,
  loyaltyRewards,
  Htag = 'h1',
  className,
  isSet,
  deviceType,
  ...htmlProps
}) => {
  const skuText = `ЛМ ${productId}`;

  const copyClick = () => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementBlock: isSet ? 'komplektyDetail' : 'productDetail',
      elementName: 'copySKU',
      elementText: skuText,
      elementType: 'button',
    });
    navigator.clipboard.writeText(productId);
    showNotification({
      templateId: NotificationTemplates.CopySKU,
    });
  };

  return (
    <div className={className} {...htmlProps}>
      <LayoutControlledTypography as="div" variant="caption" className={articleContainer}>
        {skuText}{' '}
        <CopyIcon data-qa={DATA_QA_COPY_BUTTON} size="s" className={copyIconClass} onClick={copyClick} />
      </LayoutControlledTypography>
      {loyaltyRewards && (
        <MarkerLayout>
          <Marker
            data-qa={DATA_QA_LOYALTY_REWARDS_MARKER}
            iconLeft={<BonusIcon />}
            background="secondary-red-dark"
            variant="angular"
            color="text-primary-invert"
            size="l"
            text={loyaltyRewards.label}
          />
        </MarkerLayout>
      )}
      <LayoutControlledTypography
        as={Htag}
        variant="m"
        size="body_accent"
        className={displayedNameContainer}
        data-qa={DATA_QA_PRODUCT_NAME}
      >
        {displayedName.slice(0, 120)}
      </LayoutControlledTypography>
    </div>
  );
};

export const mapStoreToProps = (store: RootReducer): ProductTitleMappedState => {
  const productId = getProductId(store) ?? '';
  const displayedName = getDisplayedName(store) ?? '';
  const loyaltyRewards = selectLoyaltyRewardData(store) ?? null;
  const isSet = isProductSet(store);
  const deviceType = getDeviceType(store);

  return {
    productId,
    displayedName,
    loyaltyRewards,
    isSet,
    deviceType,
  };
};

export const PdpProductTitle = connect(mapStoreToProps)(
  ({
    dispatch,
    ...restProps
  }: { dispatch: Dispatch<Action> } & React.ComponentProps<typeof ProductTitle>) => (
    <ProductTitle {...restProps} />
  ),
);
